import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Send } from '@mui/icons-material';
import { useMutation, useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { emailTemplateApi, invoiceApi, reservationApi } from '../../services/api';
import { EmailPreview, EmailTemplate, EmailTemplateType } from '../../types/emailTemplates';

interface EmailSendDialogProps {
  open: boolean;
  onClose: () => void;
  documentType: 'reservation' | 'invoice';
  documentId: string;
  documentLabel?: string;
}

const TEMPLATE_TYPE_BY_DOCUMENT: Record<EmailSendDialogProps['documentType'], EmailTemplateType> = {
  reservation: 'ORDER_CONFIRMATION',
  invoice: 'INVOICE',
};

export const EmailSendDialog: React.FC<EmailSendDialogProps> = ({ open, onClose, documentType, documentId, documentLabel }) => {
  const { t } = useTranslation();
  const templateType = TEMPLATE_TYPE_BY_DOCUMENT[documentType];

  const [selectedTemplateId, setSelectedTemplateId] = useState<number | ''>('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachPdf, setAttachPdf] = useState(true);
  const [attachmentFileName, setAttachmentFileName] = useState<string | null>(null);
  const [sendResult, setSendResult] = useState<{ success: boolean; message: string } | null>(null);

  const templatesQuery = useQuery<EmailTemplate[]>(
    ['emailTemplates', templateType],
    () => emailTemplateApi.getAll(templateType),
    { enabled: open }
  );

  const activeTemplates = useMemo(
    () => (templatesQuery.data ?? []).filter((template) => template.active),
    [templatesQuery.data]
  );

  useEffect(() => {
    if (!open) {
      setSelectedTemplateId('');
      setRecipientEmail('');
      setSubject('');
      setBody('');
      setAttachmentFileName(null);
      setSendResult(null);
    }
  }, [open]);

  const previewMutation = useMutation(
    (templateId: number) =>
      documentType === 'reservation'
        ? reservationApi.previewEmail(documentId, templateId)
        : invoiceApi.previewEmail(documentId, templateId),
    {
      onSuccess: (preview: EmailPreview) => {
        setRecipientEmail(preview.recipientEmail || '');
        setSubject(preview.subject);
        setBody(preview.body);
        setAttachPdf(preview.attachPdf);
        setAttachmentFileName(preview.attachmentFileName);
        setSendResult(null);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('emailSend.previewFailed', 'Failed to load template preview'));
      },
    }
  );

  const sendMutation = useMutation(
    () => {
      const payload = {
        templateId: selectedTemplateId === '' ? null : selectedTemplateId,
        recipientEmail,
        subject,
        body,
        attachPdf,
      };
      return documentType === 'reservation'
        ? reservationApi.sendEmail(documentId, payload)
        : invoiceApi.sendEmail(documentId, payload);
    },
    {
      onSuccess: (result) => {
        setSendResult(result);
        if (result.success) {
          toast.success(result.message);
        } else {
          toast.error(result.message);
        }
      },
      onError: (error: any) => {
        const message = error.response?.data?.error || t('emailSend.sendFailed', 'Failed to send email');
        setSendResult({ success: false, message });
        toast.error(message);
      },
    }
  );

  const handleTemplateChange = (templateId: number) => {
    setSelectedTemplateId(templateId);
    previewMutation.mutate(templateId);
  };

  const handleTemplateSelectChange = (value: number | '') => {
    if (value === '') {
      setSelectedTemplateId('');
      setRecipientEmail('');
      setSubject('');
      setBody('');
      setAttachmentFileName(null);
      setSendResult(null);
      return;
    }
    handleTemplateChange(value);
  };

  useEffect(() => {
    if (open && activeTemplates.length === 1 && selectedTemplateId === '') {
      handleTemplateChange(activeTemplates[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeTemplates, selectedTemplateId]);

  const handleSend = () => {
    if (!recipientEmail.trim()) {
      toast.error(t('emailSend.recipientRequired', 'Enter a recipient email address'));
      return;
    }
    if (!subject.trim() || !body.trim()) {
      toast.error(t('emailSend.subjectAndBodyRequired', 'Enter a subject and body'));
      return;
    }
    setSendResult(null);
    sendMutation.mutate();
  };

  const canSend = !previewMutation.isLoading && !sendMutation.isLoading;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('emailSend.title', 'Send Email')}
        {documentLabel ? (
          <Typography variant="body2" color="text.secondary">
            {documentLabel}
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {activeTemplates.length === 0 && !templatesQuery.isLoading ? (
            <Alert severity="info">
              {t(
                'emailSend.noTemplates',
                'No email templates yet for this document type. Write your own email below, or create a template in Settings > Email Templates.'
              )}
            </Alert>
          ) : (
            <TextField
              select
              fullWidth
              label={t('emailSend.template', 'Template')}
              value={selectedTemplateId}
              onChange={(e) => handleTemplateSelectChange(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <MenuItem value="">{t('emailSend.noTemplateOption', "Don't use a template — write my own")}</MenuItem>
              {activeTemplates.map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          {previewMutation.isLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <TextField
                fullWidth
                label={t('emailSend.to', 'To')}
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
              />
              <TextField
                fullWidth
                label={t('emailSend.subject', 'Subject')}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
              <TextField
                fullWidth
                multiline
                minRows={8}
                label={t('emailSend.body', 'Body')}
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
              <FormControlLabel
                control={<Switch checked={attachPdf} onChange={(e) => setAttachPdf(e.target.checked)} />}
                label={
                  attachmentFileName
                    ? t('emailSend.attachFile', 'Attach {{fileName}}', { fileName: attachmentFileName })
                    : t('emailSend.attachPdf', 'Attach PDF')
                }
              />
            </>
          )}

          {sendResult && (
            <Alert severity={sendResult.success ? 'success' : 'error'}>{sendResult.message}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel', 'Cancel')}</Button>
        <Button
          variant="contained"
          startIcon={sendMutation.isLoading ? <CircularProgress size={18} /> : <Send />}
          disabled={!canSend}
          onClick={handleSend}
        >
          {t('emailSend.send', 'Send')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
